import L from 'leaflet'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

const MIN_QUERY_LENGTH = 3
const SEARCH_LIMIT = 20

const initLocationPicker = (root) => {
  const searchInput = root.querySelector('[data-location-search]')
  const resultsEl = root.querySelector('[data-location-results]')
  const selectedEl = root.querySelector('[data-location-selected]')
  const selectedText = root.querySelector('[data-location-selected-text]')
  const selectedMeta = root.querySelector('[data-location-selected-meta]')
  const clearBtn = root.querySelector('[data-location-clear]')
  const mapEl = root.querySelector('[data-location-map-canvas]')
  const dropBtn = root.querySelector('[data-location-drop]')
  const dropText = root.querySelector('[data-location-drop-text]')
  const geoapifyKey = root.dataset.geoapifyKey || ''

  const inputs = {
    lat: root.querySelector('[data-location-lat]'),
    lng: root.querySelector('[data-location-lng]'),
    place: root.querySelector('[data-location-place]'),
    label: root.querySelector('[data-location-label]'),
    street: root.querySelector('[data-location-street]'),
    house: root.querySelector('[data-location-house]'),
    city: root.querySelector('[data-location-city]'),
    state: root.querySelector('[data-location-state]'),
    region: root.querySelector('[data-location-region]'),
    county: root.querySelector('[data-location-county]'),
    district: root.querySelector('[data-location-district]'),
    postcode: root.querySelector('[data-location-postcode]'),
    country: root.querySelector('[data-location-country]'),
    place_id: root.querySelector('[data-location-place-id]'),
    plus_code: root.querySelector('[data-location-plus-code]'),
    suburb: root.querySelector('[data-location-suburb]'),
    municipality: root.querySelector('[data-location-municipality]'),
    state_code: root.querySelector('[data-location-state-code]'),
    category: root.querySelector('[data-location-category]'),
    formatted: root.querySelector('[data-location-formatted]'),
    source: root.querySelector('[data-location-source]'),
    timezone: root.querySelector('[data-location-timezone]')
  }

  if (!searchInput || !resultsEl || !selectedEl || !mapEl || !dropBtn) {
    return
  }

  let map = null
  let marker = null
  let dropPinMode = false
  let searchTimeout = null
  let latestQueryId = 0

  // Reset all hidden inputs and selected UI state.
  const clearLocation = () => {
    Object.values(inputs).forEach((el) => {
      if (el) el.value = ''
    })
    selectedText.textContent = ''
    selectedEl.classList.remove('is-visible')
    if (marker && map) {
      map.removeLayer(marker)
      marker = null
    }
  }

  // Update the selected location display text and meta.
  const updateSelected = (label, meta) => {
    if (label) {
      selectedText.textContent = label
      if (selectedMeta) {
        selectedMeta.textContent = meta || ''
        selectedMeta.style.display = meta ? 'block' : 'none'
      }
      selectedEl.classList.add('is-visible')
    } else {
      selectedText.textContent = ''
      if (selectedMeta) {
        selectedMeta.textContent = ''
        selectedMeta.style.display = 'none'
      }
      selectedEl.classList.remove('is-visible')
    }
  }

  const buildLabel = (props, lat, lng) => {
    if (!props) {
      return `Pin (${lat.toFixed(5)}, ${lng.toFixed(5)})`
    }
    const streetLine = [props.street || props.address_line1, props.housenumber]
      .filter(Boolean)
      .join(' ')
    const cityLine = props.city || props.state || props.region || ''
    const parts = [streetLine, cityLine].filter(Boolean)
    if (parts.length > 0) {
      return parts.join(', ')
    }
    return props.formatted || props.name || props.label || `Pin (${lat.toFixed(5)}, ${lng.toFixed(5)})`
  }

  const buildMeta = (props) => {
    if (!props) {
      return ''
    }
    const area = props.county || props.district || props.suburb || props.municipality || ''
    return [area, props.country, props.postcode].filter(Boolean).join(', ')
  }

  // Apply a Geoapify feature to hidden inputs and map UI.
  const applyFeature = (feature, options = {}) => {
    if (!feature || !feature.geometry?.coordinates) {
      return
    }
    const [lng, lat] = feature.geometry.coordinates
    const props = feature.properties || {}
    const label = buildLabel(props, lat, lng)
    const meta = buildMeta(props)

    inputs.lat.value = lat
    inputs.lng.value = lng
    const source = options.source || props.datasource?.sourcename || 'search'
    const finalLabel = props.formatted || label
    if (inputs.place) inputs.place.value = finalLabel
    if (inputs.label) inputs.label.value = finalLabel
    inputs.street.value = props.street || props.address_line1 || props.name || ''
    inputs.house.value = props.housenumber || ''
    inputs.city.value = props.city || ''
    inputs.state.value = props.state || ''
    inputs.region.value = props.region || ''
    inputs.county.value = props.county || ''
    inputs.district.value = props.district || ''
    inputs.postcode.value = props.postcode || ''
    inputs.country.value = props.country || ''
    if (inputs.place_id) inputs.place_id.value = props.place_id || ''
    if (inputs.plus_code) inputs.plus_code.value = props.plus_code || ''
    if (inputs.suburb) inputs.suburb.value = props.suburb || ''
    if (inputs.municipality) inputs.municipality.value = props.municipality || ''
    if (inputs.state_code) inputs.state_code.value = props.state_code || ''
    if (inputs.category) inputs.category.value = props.category || ''
    if (inputs.formatted) inputs.formatted.value = props.formatted || ''
    if (inputs.source) inputs.source.value = source
    // Geoapify names the place's IANA zone in the same answer, so the creator's
    // timezone costs no request of its own. A dropped pin has none until its
    // reverse lookup lands, and then this runs again.
    if (inputs.timezone) inputs.timezone.value = props.timezone?.name || ''

    updateSelected(label, meta)
    document.dispatchEvent(new CustomEvent('location:changed'))
    resultsEl.style.display = 'none'
    searchInput.value = ''

    if (map) {
      const latlng = [lat, lng]
      if (!marker) {
        marker = L.marker(latlng).addTo(map)
      } else {
        marker.setLatLng(latlng)
      }
      const nextZoom = options.preserveZoom ? Math.max(map.getZoom(), 12) : 12
      map.setView(latlng, nextZoom)
    }
  }

  // Reverse geocode a lat/lng to a Geoapify feature.
  const reverseGeocode = async (lat, lng) => {
    if (!geoapifyKey) {
      return null
    }
    try {
      const url = `https://api.geoapify.com/v1/geocode/reverse?lat=${encodeURIComponent(
        lat
      )}&lon=${encodeURIComponent(lng)}&limit=1&format=geojson&apiKey=${encodeURIComponent(
        geoapifyKey
      )}`
      const response = await fetch(url)
      if (!response.ok) {
        return null
      }
      const data = await response.json()
      return data?.features?.[0] || null
    } catch (error) {
      console.warn('Reverse geocode failed:', error)
      return null
    }
  }

  // Search Geoapify with debouncing and cancellation by queryId.
  const searchGeoapify = async (query, queryId) => {
    if (!geoapifyKey) {
      return []
    }
    try {
      const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
        query
      )}&limit=${SEARCH_LIMIT}&format=geojson&apiKey=${encodeURIComponent(geoapifyKey)}`
      const response = await fetch(url)
      if (!response.ok) {
        return []
      }
      const data = await response.json()
      if (queryId !== latestQueryId) {
        return null
      }
      return Array.isArray(data.features) ? data.features : []
    } catch (error) {
      console.warn('Geoapify search failed:', error)
      return []
    }
  }

  // Render the search results dropdown.
  const renderResults = (features) => {
    resultsEl.innerHTML = ''
    if (!features || !features.length) {
      resultsEl.style.display = 'none'
      return
    }
    features.forEach((feature) => {
      const props = feature.properties || {}
      const label = buildLabel(props, 0, 0)
      const meta = buildMeta(props)

      const item = document.createElement('button')
      item.type = 'button'
      item.className = 'location-picker-result'
      const labelEl = document.createElement('span')
      labelEl.className = 'location-picker-result-label'
      labelEl.textContent = label
      item.appendChild(labelEl)
      if (meta) {
        const metaEl = document.createElement('span')
        metaEl.className = 'location-picker-result-meta'
        metaEl.textContent = meta
        item.appendChild(metaEl)
      }
      item.addEventListener('mousedown', (event) => {
        event.preventDefault()
        applyFeature(feature, { source: 'search' })
        const errEl = root.querySelector('small')
        if (errEl) {
          errEl.classList.add('d-none')
          errEl.innerText = ''
        }
      })
      resultsEl.appendChild(item)
    })
    resultsEl.style.display = 'block'
  }

  // Initialize Leaflet map and map click handler for pin drop.
  const initMap = () => {
    delete L.Icon.Default.prototype._getIconUrl
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: markerIcon2x,
      iconUrl: markerIcon,
      shadowUrl: markerShadow
    })

    map = L.map(mapEl, { zoomControl: false }).setView([39.8283, -98.5795], 4)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(map)
    L.control.zoom({ position: 'topright' }).addTo(map)

    map.on('click', async (event) => {
      if (!dropPinMode) {
        return
      }
      const { lat, lng } = event.latlng
      dropPinMode = false
      dropBtn.classList.remove('is-active')
      if (dropText) {
        dropText.textContent = 'Drop Pin'
      }

      applyFeature(
        {
          geometry: { coordinates: [lng, lat] },
          properties: { label: `Pin (${lat.toFixed(5)}, ${lng.toFixed(5)})` }
        },
        { preserveZoom: true }
      )

      const feature = await reverseGeocode(lat, lng)
    if (feature) {
      // console.log('Reverse geocode feature:', feature)
      applyFeature(feature, { preserveZoom: true, source: 'pin' })
      return
    }
    })
  }

  dropBtn.addEventListener('click', () => {
    dropPinMode = !dropPinMode
    dropBtn.classList.toggle('is-active', dropPinMode)
    if (dropText) {
      dropText.textContent = dropPinMode ? 'Click on the map' : 'Drop Pin'
    }
  })

  searchInput.addEventListener('input', () => {
    const query = searchInput.value.trim()
    if (query.length < MIN_QUERY_LENGTH) {
      latestQueryId += 1
      resultsEl.style.display = 'none'
      return
    }
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }
    const queryId = ++latestQueryId
    searchTimeout = setTimeout(async () => {
      const features = await searchGeoapify(query, queryId)
      if (features) {
        renderResults(features)
      }
    }, 300)
  })

  searchInput.addEventListener('blur', () => {
    setTimeout(() => {
      resultsEl.style.display = 'none'
    }, 200)
  })

  clearBtn.addEventListener('click', (event) => {
    event.preventDefault()
    clearLocation()
    searchInput.value = ''
  })

  // Hydrate from old values if present.
  if (inputs.lat.value && inputs.lng.value) {
    const lat = parseFloat(inputs.lat.value)
    const lng = parseFloat(inputs.lng.value)
    const label = inputs.place?.value || inputs.label?.value || 'Selected location'
    // The zone travels with the place: without it, a form sent back for an
    // unrelated error would come back with the timezone silently emptied.
    const timezone = inputs.timezone?.value ? { name: inputs.timezone.value } : undefined
    initMap()
    applyFeature({
      geometry: { coordinates: [lng, lat] },
      properties: { label, timezone }
    })
  } else {
    initMap()
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const pickers = document.querySelectorAll('[data-location-picker]')
  pickers.forEach(initLocationPicker)
})
